import { useEffect, useMemo, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import type { CompanyProfilePersonDto } from '@/api/companies'
import { useCompanyProfile } from '@/hooks/useCompanyProfile'
import { usePageHeader } from '@/hooks/usePageHeader'
import { routes } from '@/utils/routes'
import { ContactPeekFlyout } from '@/components/contacts/ContactPeekFlyout'
import { DataGrid } from '@/components/common/grids/DataGrid'
import { NonProspectSwitch } from '@/components/common/NonProspectSwitch'
import { CompanyAboutTab } from './CompanyAboutTab'
import { CompanyIdentity } from './CompanyIdentity'
import { CompanyMeasures } from './CompanyMeasures'
import { CompanyPipelineBand } from './CompanyPipelineBand'
import { companyPeopleColumns } from './companyPeopleColumns'

/** Overview is the default and is encoded as no trailing segment, matching the contact page. */
const TABS = ['overview', 'about'] as const
export type CompanyTabKey = (typeof TABS)[number]

interface Props {
  clientCompanyId: string
  workspaceId: string
  companyId: string
  tab?: string
}

/**
 * One targeted company as this workspace knows it (ORB-623).
 *
 * The roster is the body of the page and the facts are a strip above it, which is the opposite of
 * how a CRM account page is laid out. That is deliberate: a workspace typically sees a handful of
 * a company's people, so a layout that leads with the company implies a completeness this page
 * cannot deliver.
 *
 * Fetching lives in useCompanyProfile and the grid's columns in companyPeopleColumns, leaving this
 * file as composition.
 */
export function CompanyProfilePage({ clientCompanyId, workspaceId, companyId, tab }: Props) {
  const { setHeader } = usePageHeader()
  const location = useLocation()
  const { profile, loading, error, notFound, refresh } = useCompanyProfile(clientCompanyId, workspaceId, companyId)
  const [showNonProspects, setShowNonProspects] = useState(false)
  const [selected, setSelected] = useState<CompanyProfilePersonDto | null>(null)

  const activeTab: CompanyTabKey = (TABS as readonly string[]).includes(tab || '')
    ? (tab as CompanyTabKey)
    : 'overview'

  // Both are about one company. Leaving the toggle on across a change strands the roster: a
  // company with no non-prospects filters to empty and the switch that would undo it is not
  // rendered, because it only appears when there are some.
  useEffect(() => {
    setSelected(null)
    setShowNonProspects(false)
  }, [companyId])

  useEffect(() => {
    const scoped = routes.scoped(clientCompanyId, workspaceId)
    setHeader({
      items: [
        // Companies, not People — a company is not a person, and this now points at a real list
        // rather than the contacts view it used to borrow.
        { text: 'Companies', href: scoped.leadsCompanies() },
        { text: profile?.Name || 'Company' },
      ],
    })
  }, [clientCompanyId, workspaceId, profile?.Name])

  const routeToTab = (next: CompanyTabKey) => {
    const scoped = routes.scoped(clientCompanyId, workspaceId)
    location.route(scoped.companyProfile(companyId, next === 'overview' ? undefined : next))
  }

  // Non-prospects ship on the wire and are hidden here, the same way the lead grids do it, so the
  // two surfaces cannot disagree about who counts.
  const visiblePeople = useMemo(() => {
    const people = profile?.People ?? []
    return showNonProspects
      ? people.filter(p => p.IsNonProspect)
      : people.filter(p => !p.IsNonProspect)
  }, [profile?.People, showNonProspects])

  const columns = useMemo(() => companyPeopleColumns(), [])

  if (loading && !profile) {
    return (
      <div class="p-6 flex items-center gap-3 text-slate-600 dark:text-slate-300">
        <div class="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        Loading company…
      </div>
    )
  }

  if (notFound) {
    return (
      <div class="p-10 text-center">
        <div class="font-semibold text-slate-800 dark:text-slate-100">No record of this company</div>
        <div class="mt-1 text-sm text-slate-500 dark:text-slate-400">
          This workspace hasn't targeted or heard from anyone here.
        </div>
      </div>
    )
  }

  if (error && !profile) return <div class="p-6 text-red-600 dark:text-red-400">{error}</div>
  if (!profile) return null

  return (
    <section class="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
      <CompanyIdentity profile={profile} />
      <CompanyMeasures measures={profile.Measures} />
      <CompanyPipelineBand
        profile={profile}
        clientCompanyId={clientCompanyId}
        workspaceId={workspaceId}
        onLinked={refresh}
      />

      <div class="border-b border-slate-200 dark:border-slate-800 px-3">
        <nav class="-mb-px flex gap-1" aria-label="Company sections">
          {TABS.map(key => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              onClick={() => routeToTab(key)}
              class={`relative px-3 py-2.5 text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'text-slate-900 dark:text-slate-100'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              {key === 'overview' ? 'Overview' : 'About'}
              <span
                class={`absolute inset-x-3 bottom-0 h-0.5 rounded-full ${
                  activeTab === key ? 'bg-primary-600' : 'bg-transparent'
                }`}
              />
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' ? (
        // The padding the deleted header strip used to carry (ORB-715). `pt-4` keeps the tab bar's
        // bottom border off the card's top border; `px-5 pb-5` is the gutter the About tab uses, so the
        // card sits on the page's own inset rather than bleeding to the section edge. It does mean the
        // grid's title lands inboard of the CompanyIdentity heading above it, which is the trade.
        <div class="px-5 pt-4 pb-5">
          <DataGrid<CompanyProfilePersonDto>
            // "Contacts" like every other person grid in the app, even though this page's own prose
            // says "people". One noun per row type is the point of the convention; a page keeping its
            // own word for the same rows is how the grids drifted apart in the first place.
            title="Contacts"
            subtitle="Only contacts this workspace targeted or heard from."
            data={visiblePeople}
            columns={columns}
            getRowKey={p => `${p.AnchorType}:${p.AnchorId}`}
            onRowClick={setSelected}
            scrollable
            columnVisibilityKey="orbitalx-company-people-columns"
            emptyMessage={
              showNonProspects
                ? 'No non-prospects from this company.'
                : 'Nobody from this company has been targeted in this workspace yet.'
            }
            toolbarActions={
              profile.Measures.NonProspectCount > 0 && (
                /* Only the non-prospect switch: every person here is already someone this
                   workspace targeted or heard from, so the audience axis has nothing to slice.

                   The count goes only on the "off" state, where it says how many rows turning it on
                   would reveal. Left on while the toggle is on, it would sit beside the strip's own
                   "N results" showing the identical number, because the visible rows ARE the
                   non-prospects then. */
                <NonProspectSwitch
                  showNonProspects={showNonProspects}
                  onToggle={() => setShowNonProspects(v => !v)}
                  nonProspectCount={showNonProspects ? undefined : profile.Measures.NonProspectCount}
                />
              )
            }
          />
        </div>
      ) : (
        <CompanyAboutTab profile={profile} />
      )}

      {selected && (
        <ContactPeekFlyout
          anchorType={selected.AnchorType === 'Lead' ? 'lead' : 'audience'}
          anchorId={selected.AnchorId}
          clientCompanyId={clientCompanyId}
          workspaceId={workspaceId}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  )
}
